import { KeyboardEvent, useEffect, useRef, useState } from 'react';

import useOrgChart from '../OrgChartContext';
import { SELECTED_EMPLOYEE_QUERY_PARAM } from '../constants';
import { FlattenedResult, SearchResult, UseOrgChartSearchOptions } from './interfaces';

export function useOrgChartSearch({
  onSearchTermChange,
  maxResults = 5,
}: UseOrgChartSearchOptions = {}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [flattenedResults, setFlattenedResults] = useState<FlattenedResult[]>([]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const { employees, findAllPaths, expandToEmployee, scrollToEmployee } = useOrgChart();

  // Find direct manager from a path
  const getReportsTo = (path: string[]): string | null => {
    if (path.length < 2) return null;
    return path[path.length - 2]; // Second-to-last element is the direct manager
  };

  // Flatten search results for keyboard navigation
  useEffect(() => {
    const flattened: FlattenedResult[] = [];

    searchResults.forEach((result) => {
      result.paths.forEach((pathInfo) => {
        flattened.push({
          employeeId: result.employee.id,
          path: pathInfo.path,
          reportsTo: pathInfo.reportsTo,
        });
      });
    });

    setFlattenedResults(flattened);

    // Reset highlighted index when results change
    setHighlightedIndex(-1);
  }, [searchResults]);

  // Scroll to highlighted item
  useEffect(() => {
    if (highlightedIndex >= 0 && resultsContainerRef.current) {
      const highlightedItem = flattenedResults[highlightedIndex];
      if (!highlightedItem) return;

      const itemKey = getItemKey(highlightedItem.employeeId, highlightedItem.path);
      const element = itemRefs.current[itemKey];

      if (element && resultsContainerRef.current) {
        // Manual scroll calculation to avoid page scrolling
        const container = resultsContainerRef.current;
        const containerTop = container.scrollTop;
        const containerHeight = container.clientHeight;
        const containerBottom = containerTop + containerHeight;

        const elementTop = element.offsetTop;
        const elementHeight = element.clientHeight;
        const elementBottom = elementTop + elementHeight;

        if (elementTop < containerTop) {
          // Element is above visible area
          container.scrollTop = elementTop;
        } else if (elementBottom > containerBottom) {
          // Element is below visible area
          container.scrollTop = elementBottom - containerHeight;
        }
      }
    }
  }, [highlightedIndex, flattenedResults]);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!isDropdownOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < flattenedResults.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < flattenedResults.length) {
          const selected = flattenedResults[highlightedIndex];
          handleSelectPath(selected.employeeId, selected.path);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        break;
    }
  };

  // Handle search term changes
  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    if (onSearchTermChange) {
      onSearchTermChange(term);
    }

    if (term.trim() === '') {
      setSearchResults([]);
      setIsDropdownOpen(false);
      return;
    }

    // Perform search
    const results: SearchResult[] = [];
    const matchedEmployees = new Set<string>();

    const normalizedTerm = term.toLowerCase();

    for (const employee of employees) {
      if (
        employee.name.toLowerCase().includes(normalizedTerm) ||
        employee.title.toLowerCase().includes(normalizedTerm)
      ) {
        if (!matchedEmployees.has(employee.id)) {
          const paths = findAllPaths(employee.id);

          if (paths.length > 0) {
            const formattedPaths = paths.map((p) => ({
              path: p.path,
              reportsTo: getReportsTo(p.path),
            }));

            results.push({
              employee,
              paths: formattedPaths,
            });

            matchedEmployees.add(employee.id);
          }
        }
      }

      if (results.length >= maxResults) break;
    }

    setSearchResults(results);
    setIsDropdownOpen(results.length > 0);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectPath = (employeeId: string, path: string[]) => {
    const url = new URL(window.location.href);
    url.searchParams.set(SELECTED_EMPLOYEE_QUERY_PARAM, employeeId);
    window.history.pushState({}, '', url.toString());

    // Call the context method to expand to the employee
    expandToEmployee(employeeId, path);
    scrollToEmployee(employeeId);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  // Find the index of a result in the flattened array
  const getResultIndex = (employeeId: string, path: string[]) => {
    return flattenedResults.findIndex(
      (item) =>
        item.employeeId === employeeId &&
        path.length === item.path.length &&
        path.every((id, i) => id === item.path[i])
    );
  };

  const getItemKey = (employeeId: string, path: string[]) => {
    return `${employeeId}-${path.join('-')}`;
  };

  const registerItemRef = (
    employeeId: string,
    path: string[],
    element: HTMLButtonElement | null
  ) => {
    const key = getItemKey(employeeId, path);
    itemRefs.current[key] = element;
  };

  return {
    // State
    searchTerm,
    searchResults,
    isDropdownOpen,
    highlightedIndex,
    flattenedResults,

    // Refs
    dropdownRef,
    resultsContainerRef,

    // Event handlers
    handleSearchTermChange,
    handleKeyDown,
    handleSelectPath,

    // Utility functions
    getResultIndex,
    getItemKey,
    registerItemRef,
    setHighlightedIndex,
  };
}
